import { useState, useEffect, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useLanguage } from "../context/LanguageContext";


function useCountUp(target, duration = 2000, started = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started || target === 0) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [target, started, duration]);

  return count;
}

function StatCard({
  icon,
  label,
  value,
  suffix = "",
  delay = 0,
  started,
  loading,
}) {
  const count = useCountUp(value, 2000, started);

  return (
    <div
      style={{
        textAlign: "center",
        padding: "40px 24px",
        opacity: started ? 1 : 0,
        transform: started ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {/* Icon circle */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
          backdropFilter: "blur(8px)",
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 26, color: "#fff" }}
        >
          {icon}
        </span>
      </div>

      {/* Counter */}
      {loading ? (
        <div
          style={{
            height: 52,
            width: 120,
            margin: "0 auto 10px",
            borderRadius: 8,
            background: "rgba(255,255,255,0.1)",
            animation: "pulse 1.5s ease-in-out infinite",
          }}
        />
      ) : (
        <div
          style={{
            fontFamily: "Geist, sans-serif",
            fontSize: "clamp(40px, 5vw, 56px)",
            fontWeight: 700,
            color: "#fff",
            lineHeight: 1,
            letterSpacing: "-0.03em",
            marginBottom: 10,
          }}
        >
          {count.toLocaleString()}
          {suffix}
        </div>
      )}

      {/* Label */}
      <p
        style={{
          fontFamily: "Geist, sans-serif",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.6)",
          margin: 0,
        }}
      >
        {label}
      </p>
    </div>
  );
}

export default function StatsSection() {
  const [stats, setStats] = useState({
    places: 0,
    categories: 0,
    photos: 0,
    likes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const load = async () => {
      try {
        const [placesSnap, catsSnap] = await Promise.all([
          getDocs(collection(db, "places")),
          getDocs(collection(db, "categories")),
        ]);
        const places = placesSnap.docs.map((d) => d.data());
        const totalPhotos = places.reduce(
          (sum, p) => sum + (p.images?.length || 0),
          0,
        );
        const totalLikes = places.reduce((sum, p) => sum + (p.likes || 0), 0);
        setStats({
          places: places.length,
          categories: catsSnap.size,
          photos: totalPhotos,
          likes: totalLikes,
        });
      } catch (err) {
        console.error("Error loading stats:", err);
      }
      setLoading(false);
    };
    load();
  }, []);
  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [loading]);

const items = [
  { icon: "location_on", label: t("stats_label_destinations"), value: stats.places, suffix: "+" },
  { icon: "category", label: t("stats_label_categories"), value: stats.categories, suffix: "" },
  { icon: "photo_library", label: t("stats_label_photos"), value: stats.photos, suffix: "+" },
  { icon: "favorite", label: t("stats_label_likes"), value: stats.likes, suffix: "+" },
];

  return (
    <section
      ref={ref}
      style={{
        position: "relative",
        overflow: "hidden",
        background: "#0a0a0a",
      }}
    >
      {/* Mountain silhouette SVG background */}
      <svg
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0.06,
          pointerEvents: "none",
        }}
      >
        <path
          d="M0,320 L0,200 L120,140 L200,180 L320,80 L420,130 L520,60 L600,100 L720,20 L820,80 L920,40 L1020,90 L1120,50 L1200,100 L1320,60 L1440,120 L1440,320 Z"
          fill="#ffffff"
        />
        <path
          d="M0,320 L0,240 L100,200 L200,220 L340,160 L440,190 L560,140 L660,170 L780,120 L880,150 L980,110 L1080,140 L1180,100 L1280,130 L1380,110 L1440,140 L1440,320 Z"
          fill="#ffffff"
          opacity="0.5"
        />
      </svg>

      {/* Subtle grid overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
          linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
        `,
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "relative",
          maxWidth: 1280,
          margin: "0 auto",
          padding: "80px 64px",
        }}
        className="stats-inner"
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <p
            style={{
              fontFamily: "Geist, sans-serif",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
              marginBottom: 12,
            }}
          >
            {t("stats_eyebrow")}
          </p>
          <h2
            style={{
              fontFamily: "Geist, sans-serif",
              fontSize: "clamp(28px, 4vw, 42px)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: "#fff",
              margin: 0,
            }}
          >
            {t("stats_title")}
          </h2>
        </div>

        {/* Stats grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 0,
          }}
          className="stats-grid"
        >
          {items.map((item, i) => (
            <div
              key={item.label}
              style={{
                borderRight:
                  i < items.length - 1
                    ? "1px solid rgba(255,255,255,0.08)"
                    : "none",
              }}
              className="stat-cell"
            >
              <StatCard
                key={item.label}
                icon={item.icon}
                label={item.label}
                value={item.value}
                suffix={item.suffix}
                delay={i * 120}
                started={started}
                loading={loading}
              />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @media (max-width: 1024px) {
          .stats-inner { padding: 64px 32px !important; }
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .stat-cell:nth-child(2) { border-right: none !important; }
          .stat-cell:nth-child(3) { border-right: 1px solid rgba(255,255,255,0.08) !important; border-top: 1px solid rgba(255,255,255,0.08) !important; }
          .stat-cell:nth-child(4) { border-top: 1px solid rgba(255,255,255,0.08) !important; }
        }
        @media (max-width: 640px) {
          .stats-inner { padding: 48px 20px !important; }
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </section>
  );
}
